import styled from '@emotion/styled'
import React, { useState } from 'react'
import toastr from 'toastr'
import { useMutation } from '../../../api/graphql'
import Routes from '../../../startup/routes'
import { extractDbIdFromGraphqlId } from '../../../util/string'
import {
  BUSINESS_SUBSCRIPTION_CHECKOUT_MUTATION,
  BusinessSubscriptionCheckoutInput,
  BusinessSubscriptionCheckoutResponse,
} from './BusinessCalendarCheckoutMutation'
import OrganizationSelector from './OrganizationSelector'
import PlanSelector, { Plan } from './PlanSelector'

type Step = 'organization' | 'plan'

const getCanceledParam = () => {
  const params = new URLSearchParams(window.location.search)
  return params.get('canceled') === 'true'
}

const BusinessCalendarOnboarding: React.FC = () => {
  const [step, setStep] = useState<Step>('organization')
  const [organizationId, setOrganizationId] = useState<string | null>(null)
  const [plan, setPlan] = useState<Plan | null>(null)
  const [showCanceledMessage, setShowCanceledMessage] = useState(getCanceledParam)

  const [checkout, { loading }] = useMutation<BusinessSubscriptionCheckoutResponse, { input: BusinessSubscriptionCheckoutInput }>(
    BUSINESS_SUBSCRIPTION_CHECKOUT_MUTATION
  )

  // Clean up canceled param from URL
  React.useEffect(() => {
    if (showCanceledMessage) {
      window.history.replaceState({}, '', window.location.pathname)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handleOrganizationSelect = (orgId: string) => {
    setOrganizationId(orgId || null)
  }

  const handlePlanSelect = (selectedPlan: Plan) => {
    setPlan(selectedPlan)
  }

  const handleNext = () => {
    if (step === 'organization' && organizationId) {
      setStep('plan')
    }
  }

  const handleBack = () => {
    if (step === 'plan') {
      setStep('organization')
    }
  }

  const handleSubmit = async () => {
    if (!organizationId || !plan) return

    const baseUrl = window.location.origin
    const orgDbId = extractDbIdFromGraphqlId(organizationId)
    const successUrl = `${baseUrl}${Routes.businessCalendarDetail(orgDbId)}?success=true`
    const cancelUrl = `${baseUrl}${Routes.businessCalendarSignup()}?canceled=true`

    const result = await checkout({
      variables: {
        input: {
          organizationId,
          plan,
          successUrl,
          cancelUrl,
        },
      },
    })

    const response = result.data?.businessSubscriptionCheckout
    if (response?.errors && response.errors.length > 0) {
      toastr.error(response.errors[0].message)
      return
    }

    if (response?.checkoutUrl) {
      // Redirect to Stripe checkout
      window.location.href = response.checkoutUrl
    }
  }

  const canProceed = () => {
    return plan !== null
  }

  const getStepNumber = () => {
    if (step === 'organization') return 1
    if (step === 'plan') return 2
    return 1
  }

  return (
    <Container>
      <Header>
        <Logo>CivicLift</Logo>
        <HeaderRight>
          <StepIndicator>
            Step {getStepNumber()} of 2
          </StepIndicator>
          <DashboardLink href="/dash">Back to Dashboard</DashboardLink>
        </HeaderRight>
      </Header>

      <ProgressBar>
        <ProgressFill style={{ width: `${(getStepNumber() / 2) * 100}%` }} />
      </ProgressBar>

      <Content>
        {showCanceledMessage && (
          <CanceledBanner>
            <CanceledText>Your checkout was canceled. No worries — you can try again when you&apos;re ready.</CanceledText>
            <CanceledDismiss onClick={() => setShowCanceledMessage(false)}>&times;</CanceledDismiss>
          </CanceledBanner>
        )}
        {step === 'organization' && (
          <OrganizationSelector
            value={organizationId}
            onChange={handleOrganizationSelect}
            onAutoAdvance={() => setStep('plan')}
          />
        )}

        {step === 'plan' && <PlanSelector value={plan} onChange={handlePlanSelect} />}
      </Content>

      <Footer>
        {step === 'organization' && (
          <BackButton as="a" href={Routes.businessCalendarDashboard()}>
            Back
          </BackButton>
        )}
        {step === 'plan' && (
          <BackButton onClick={handleBack} disabled={loading}>
            Back
          </BackButton>
        )}
        <Spacer />
        {step === 'organization' && (
          <NextButton onClick={handleNext} disabled={!organizationId}>
            Continue
          </NextButton>
        )}
        {step === 'plan' && (
          <SubmitButton onClick={handleSubmit} disabled={!canProceed() || loading}>
            {loading ? 'Processing...' : 'Start Free Trial'}
          </SubmitButton>
        )}
      </Footer>
    </Container>
  )
}

export default BusinessCalendarOnboarding

const Container = styled.div`
  min-height: 100vh;
  background: #f9fafb;
  display: flex;
  flex-direction: column;
`

const Header = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 32px;
  background: white;
  border-bottom: 1px solid #e5e7eb;
`

const Logo = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: #102638;
`

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`

const StepIndicator = styled.div`
  font-size: 14px;
  color: #6b7280;
`

const DashboardLink = styled.a`
  font-size: 13px;
  font-weight: 500;
  color: #6b7280;
  text-decoration: none;
  transition: color 0.15s ease;

  &:hover {
    color: #374151;
  }
`

const CanceledBanner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 18px;
  margin-bottom: 24px;
  background: #fef3c7;
  border: 1px solid #fde68a;
  border-radius: 10px;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`

const CanceledText = styled.p`
  font-size: 14px;
  color: #92400e;
  margin: 0;
  line-height: 1.5;
`

const CanceledDismiss = styled.button`
  background: none;
  border: none;
  font-size: 18px;
  cursor: pointer;
  color: #92400e;
  opacity: 0.7;
  padding: 0 4px;
  flex-shrink: 0;

  &:hover {
    opacity: 1;
  }
`

const ProgressBar = styled.div`
  height: 4px;
  background: #e5e7eb;
`

const ProgressFill = styled.div`
  height: 100%;
  background: linear-gradient(90deg, #3b82f6 0%, #2563eb 100%);
  transition: width 0.3s ease;
`

const Content = styled.main`
  flex: 1;
  padding: 48px 32px;
  overflow-y: auto;
`

const Footer = styled.footer`
  display: flex;
  align-items: center;
  padding: 20px 32px;
  background: white;
  border-top: 1px solid #e5e7eb;
  margin-top: auto;
`

const Spacer = styled.div`
  flex: 1;
`

const BackButton = styled.button`
  background: transparent;
  border: 2px solid #e5e7eb;
  color: #4b5563;
  font-size: 14px;
  font-weight: 600;
  padding: 12px 24px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    border-color: #9ca3af;
    color: #374151;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const SubmitButton = styled.button`
  background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
  border: none;
  color: white;
  font-size: 14px;
  font-weight: 600;
  padding: 12px 32px;
  border-radius: 8px;
  cursor: pointer;
  transition: opacity 0.2s ease;

  &:hover:not(:disabled) {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const NextButton = styled.button`
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  border: none;
  color: white;
  font-size: 14px;
  font-weight: 600;
  padding: 12px 32px;
  border-radius: 8px;
  cursor: pointer;
  transition: opacity 0.2s ease;

  &:hover:not(:disabled) {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`
